import { Injectable, OnInit } from '@angular/core';
import { AnalyticsEvent, AnalyticsEventPayloadMap, AnalyticsGlobalProps } from './models/analytics-event';
import { AnalyticsDispatcherService } from './infrastructure/analytics-dispatcher.service';
import { AnalyticsQueueService } from './infrastructure/analytics-queue.service';
import { AnalyticsConsentService } from './infrastructure/analytics-consent.service';
import { v4 as uuidv4 } from 'uuid'; // para event_id único
import { environment } from 'src/environments/environment';
import { User, UserRoleService } from 'src/app/core/services/role.service';
import { LocalStorageService } from '../services/LocalStorage.service';
import { LocalStorageKeys } from '../core/enums/LocalStorageKeys';
import { CenterInfo } from '../core/interfaces/my-workshop';
import { firstValueFrom, BehaviorSubject, catchError, of, filter, take } from 'rxjs';
import { MyAccountService } from '../core/services/my-account.service';

/**
 ============================================================
 🧠 CENTRAL ANALYTICS SERVICE (SaaS Enterprise Standard)
 ============================================================

 All analytics events MUST go through this service.
 Do NOT call providers directly (GA, Segment, etc).

 This ensures:
 - Strong typing
 - GDPR consent compliance
 - Multi-provider support
 - Event queueing before consent/provider ready
 - Global property injection

 ------------------------------------------------------------
 📌 BASIC USAGE
 ------------------------------------------------------------

private readonly analytics: AnalyticsService

 * 1. Track view event

    await this.analytics.track(AnalyticsEvent.AgendaView, {
        ...this.analytics.globalProps(),
        view_mode: ViewModeType.Calendar,
        date_range: "week"
    });

    SUCCESS REQUEST: 
    
      tap(async (resp: CreateAgendaResponse) => {

          await this.analytics.track(AnalyticsEvent.AgendaAddServiceSuccess, {
              ...this.analytics.globalProps(),
              service_id: agenda.serviceTypeId,
              service_type: agendaResp.serviceName ?? '',
              duration: agendaResp.duration ?? '',
              price: agendaResp.service?.price ?? 0,
          });
      }),

    FAIL REQUEST:

      catchError(async (err: HttpErrorResponse) => {
          const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
          const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

          await this.analytics.track(AnalyticsEvent.AgendaAddServiceFail, {
              ...this.analytics.globalProps(),
              service_type: agenda.serviceTypeId,
              error_code: errorCode,
              error_message: errorMessage,
              http_status: err.status
          });

          return throwError(() => err);
      })
 

    ERROR SIMULATION

    const fakeError = new HttpErrorResponse({
        status: 500,
        statusText: 'Internal Server Error',
        error: {
          code: 'SIMULATED_BACKEND_ERROR',
          message: 'Simulated backend failure'
        }
      });

      return throwError(() => fakeError);


  ------------------------------------------------------------
 
  ⚠️ RULES
  ------------------------------------------------------------
  - Always use AnalyticsEvent enum
  - Never send raw strings
  - Always include required props
  - Keep payload flat (no deep nested objects)
  - Do not call providers directly
 
 */

@Injectable({ providedIn: 'root' })
export class AnalyticsService {

  private currentUser: User | null = null;
  private centerInfoSubject = new BehaviorSubject<CenterInfo | null>(null);
  private centerInfoLoading = false;

  constructor(
    private readonly dispatcher: AnalyticsDispatcherService,
    private readonly queueService: AnalyticsQueueService,
    private readonly consentService: AnalyticsConsentService,
    private readonly userRoleService: UserRoleService,
    private readonly localStorageService: LocalStorageService,
    private readonly myAccountService: MyAccountService
  ) {
    this.deviceType = this.detectDevice();
    this.appVersion = '1.0.0';

    // Cargar datos del usuario actual
    this.userRoleService.currentUser$.subscribe((user) => {
      this.currentUser = user;
    });
  }


  /**
   * ============================================================
   * Cargar centerInfo de forma lazy (primera vez que se necesita)
   * ============================================================
   */
  private async loadCenterInfoIfNeeded(): Promise<CenterInfo | null> {
    // Si ya está cargado, retornar
    if (this.centerInfoSubject.value) {
      return this.centerInfoSubject.value;
    }

    // Si ya se está cargando, esperar
    if (this.centerInfoLoading) {
      return await firstValueFrom(
        this.centerInfoSubject.pipe(
          catchError(() => of(null))
        )
      );
    }

    // Cargar por primera vez
    this.centerInfoLoading = true;
    try {
      const centerInfo = await this.myAccountService.loadCenter();
      this.centerInfoSubject.next(centerInfo);
      return centerInfo;
    } catch (error) {
      console.error('Error loading center info for analytics:', error);
      this.centerInfoSubject.next(null);
      return null;
    } finally {
      this.centerInfoLoading = false;
    }
  }


  /**
   * ============================================================
   * Track any analytics event (STRONGLY TYPED)
   * ============================================================
   *
   * Type-safe mapping between event and payload.
   * Each event only accepts its correct properties.
   * ⚠️ Ahora es async para garantizar que centerInfo esté cargado
   */
  async track<K extends AnalyticsEvent>(
    event: K,
    payload: AnalyticsEventPayloadMap[K]
  ): Promise<void> {
    // Garantizar que centerInfo esté listo antes de enviar el evento
    await this.loadCenterInfoIfNeeded();
    this.dispatcher.dispatch(event, payload);
  }

  /**
   * ============================================================
   * CONSENT ACCEPTED (called from cookie banner)
   * ============================================================
   */
  enableTracking(): void {
    this.consentService.setConsent(true);
    this.queueService.setReady();
  }

  /**
   * ============================================================
   * CONSENT REJECTED
   * ============================================================
   */
  disableTracking(): void {
    this.consentService.setConsent(false);
  }


  private readonly deviceType: string;
  private readonly appVersion: string;

  globalProps(): AnalyticsGlobalProps {
    const centerInfo = this.centerInfoSubject.value;
    return {
      event_id: uuidv4(),
      timestamp: this.formatTimestamp(),
      current_session_id: this.generateSessionId(),
      current_user_id: this.getCurrentUserId(),
      user_name: this.getCurrentUser(),
      role: this.getCurrentUserRole(),
      workshop_id: this.getCurrentWorkshopId(),
      workshop_country: this.getCurrentWorkshopCountry(),
      device_type: this.deviceType,
      app_version: this.appVersion,
      screen_name: this.getCurrentScreenName(),
      group_id: this.getCurrentGroupId(),
      group_name: this.getCurrentGroupName(centerInfo),
      workshop_name: this.getCurrentCenterName(centerInfo)
    };
  }

  // --- Helpers ---

  private formatTimestamp(date: Date = new Date()): string {
    const pad = (num: number, size = 2) => String(num).padStart(size, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const ms = pad(date.getMilliseconds(), 3);

    return `${year}:${month}:${day}:${hours}:${minutes}:${seconds}:${ms}`;
  }

  private generateSessionId(): string {
    let session_id: string | null = this.localStorageService.get(LocalStorageKeys.SESSION_KEY);
    return session_id ?? '';
  }

  async startSession(): Promise<void> {
    const existingSession = this.localStorageService.get(LocalStorageKeys.SESSION_KEY);

    if (existingSession) {
      return;
    }
    const newSessionId = uuidv4();
    this.localStorageService.set(LocalStorageKeys.SESSION_KEY, newSessionId);

    const user = await this.waitForUser();

    await this.track(AnalyticsEvent.SessionStart, {
      ...this.globalProps(),
      current_user_id: user.id,
      user_name: (user as { fullName?: string }).fullName ?? '',
    });
  }


  private detectDevice(): string {
    const ua = navigator.userAgent;
    if (/wv|WebView|ReactNative/i.test(ua) || (ua.includes('Version/') && ua.includes('Mobile'))) { return 'tablet' };
    return 'web'
  }

  /**
   * Obtiene el ID del usuario actual desde el UserRoleService
   */
  private getCurrentUserId(): number | string {
    if (this.currentUser?.id) {
      return this.currentUser.id;
    }
    return 'unknown';
  }

  private getCurrentUser(): string {

    if (this.currentUser?.fullName) {
      return this.currentUser.fullName;
    }
    return 'unknown';
  }


  /**
   * Obtiene el rol primario del usuario actual.
   * Normaliza a minúsculas y devuelve el primero de la lista de roles.
   */
  private getCurrentUserRole(): string {
    if (this.currentUser?.roles && this.currentUser.roles.length > 0) {
      const primaryRole = this.currentUser.roles[0].toLowerCase();
      return primaryRole;
    }
    return 'unknown';
  }

  /**
   * Obtiene el ID del workshop (centro) del usuario actual.
   * También conocido como centerId en el backend.
   */
  private getCurrentWorkshopId(): string {
    if (this.currentUser?.centerId) {
      return String(this.currentUser.centerId);
    }
    return 'unknown';
  }

  /**
   * Obtiene el país del workshop/centro actual.
   * Se obtiene del usuario actual (ISO 3166-1 alpha-2 code).
   */
  private getCurrentWorkshopCountry(): string {
    if (this.currentUser?.country) {
      return this.currentUser.country.toUpperCase();
    }
    return 'unknown';
  }

  private getCurrentScreenName(): string {
    return window.location.pathname;
  }

  private getCurrentGroupId(): number | string {
    if (this.currentUser?.centerGroupId) {
      return this.currentUser.centerGroupId
    }
    return 'unknown';
  }

  private getCurrentGroupName(centerInfo: CenterInfo | null): string {
    if (centerInfo?.centerGroupName) {
      return centerInfo.centerGroupName
    }
    return 'unknown';
  }

  private getCurrentCenterName(centerInfo: CenterInfo | null): string {
    if (centerInfo?.name) {
      return centerInfo.name
    }
    return 'unknown';
  }

  private async waitForUser(): Promise<User> {
    return await firstValueFrom(
      this.userRoleService.currentUser$.pipe(
        filter((user): user is User => !!user),
        take(1)
      )
    );
  }


}
